import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  Refresh,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format, differenceInYears } from 'date-fns';
import { toast } from 'react-toastify';
import { publicShareApi } from '../../services/fileShareApi';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch {
    return dateString;
  }
};

const isIndefiniteShare = (share) => {
  if (share.expiryDuration === 'never') return true;
  if (!share.expiresAt || !share.createdAt) return false;
  try {
    return differenceInYears(new Date(share.expiresAt), new Date(share.createdAt)) >= 50;
  } catch {
    return false;
  }
};

const formatExpiryOption = (share) => {
  if (isIndefiniteShare(share)) return 'No expiry (indefinite)';
  switch (share.expiryDuration) {
    case '1h':
      return '1 Hour';
    case '6h':
      return '6 Hours';
    case '24h':
      return '24 Hours';
    case '7d':
      return '7 Days';
    case '30d':
      return '30 Days';
    case 'custom':
      return 'Custom';
    default:
      return share.expiryDuration || 'Unknown';
  }
};

const getLifecycle = (share) => {
  if (share.isRevoked) {
    return { key: 'revoked', label: 'Revoked', color: 'error' };
  }
  if (!share.isValid) {
    return { key: 'expired', label: 'Expired', color: 'warning' };
  }
  return { key: 'active', label: 'Active', color: 'success' };
};

const ShareLinks = () => {
  const navigate = useNavigate();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadShares = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await publicShareApi.getAllShares();
      if (response.data?.success) {
        setShares(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setError(response.data?.message || 'Failed to load share links');
        setShares([]);
      }
    } catch (err) {
      console.error('Failed to load share links:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load share links');
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const filteredShares = useMemo(() => {
    const q = search.trim().toLowerCase();
    return shares.filter((share) => {
      const lifecycle = getLifecycle(share);
      if (statusFilter !== 'all' && lifecycle.key !== statusFilter) return false;
      if (!q) return true;
      const haystack = [
        share.projectName,
        share.fileName,
        share.shareUrl,
        share.expiryDuration,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [shares, search, statusFilter]);

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <IconButton onClick={() => navigate('/file-share')} size="small" aria-label="Back to projects" sx={{ mt: 0.25 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h6">Share Links ({filteredShares.length})</Typography>
            <Typography variant="body2" color="text.secondary">
              Project, link, created date, and expiration for every public share
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={loadShares}
          disabled={loading}
          variant="outlined"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder="Search project, file, or link"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="revoked">Revoked</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={8}>
          <CircularProgress />
        </Box>
      ) : shares.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <LinkIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" gutterBottom>
            No share links yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Open a translated file in a project and use Share File Publicly to create one.
          </Typography>
          <Button
            sx={{ mt: 2, textTransform: 'none' }}
            variant="contained"
            onClick={() => navigate('/file-share')}
          >
            Back to projects
          </Button>
        </Paper>
      ) : filteredShares.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No links match your search or filter.</Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Project Name</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Share Link</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>
                  Created Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Expiration Status</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper', width: 90 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredShares.map((share) => {
                const fullUrl = `${window.location.origin}${share.shareUrl}`;
                const lifecycle = getLifecycle(share);
                const indefinite = isIndefiniteShare(share);
                const optionLabel = formatExpiryOption(share);

                return (
                  <TableRow key={share.shareId} hover>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={share.projectName}>
                        {share.projectName || 'Unknown project'}
                      </Typography>
                      {share.fileName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                          title={share.fileName}
                        >
                          {share.fileName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={fullUrl}
                          sx={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: '0.8rem' }}
                        >
                          {fullUrl}
                        </Typography>
                        <Tooltip title="Copy link">
                          <IconButton size="small" onClick={() => handleCopy(fullUrl)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{formatDate(share.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {optionLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {indefinite
                          ? 'Link works for lifetime'
                          : lifecycle.key === 'expired'
                            ? `Expired ${formatDate(share.expiresAt)}`
                            : `Expires ${formatDate(share.expiresAt)}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={lifecycle.label} color={lifecycle.color} variant="outlined" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ShareLinks;
